/**
 * The audio page's pieces. Each is presentational: state comes from `useAudioCompanion`, and nothing
 * here opens a socket, fetches a track or touches storage.
 */
import { useEffect, useRef, type CSSProperties } from 'react';

import { AUDIO_VOLUME_CATEGORIES, type AudioVolumeCategory, type AudioVolumes } from '@dimensionscore/shared';

import { parseMiniMessage, styleToCss } from '../blocks/minimessage';
import { generatedFace, skinUrl } from './head';

// ------------------------------------------------------------------------------ the head

/**
 * The player's head, 8×8, pixel-crisp. Decorative — the name beside it says who it is — so it is
 * hidden from assistive technology rather than announced twice.
 */
export function PlayerHead({ uuid, skin, size = 64 }: { readonly uuid: string; readonly skin: string | null; readonly size?: number }): React.JSX.Element {
  if (skin !== null) {
    const scale = size / 8;
    const layer = (x: number): CSSProperties => ({
      backgroundImage: `url(${skinUrl(skin)})`,
      backgroundSize: `${64 * scale}px ${64 * scale}px`,
      backgroundPosition: `-${x * scale}px -${8 * scale}px`,
    });
    return (
      <span className="au-head au-head-skin" style={{ width: size, height: size }} aria-hidden="true" data-testid="player-head" data-head="skin">
        <span style={layer(8)} />
        <span style={layer(40)} />
      </span>
    );
  }
  const pixels = generatedFace(uuid);
  return (
    <svg className="au-head" width={size} height={size} viewBox="0 0 8 8" shapeRendering="crispEdges" aria-hidden="true" focusable="false" data-testid="player-head" data-head="generated">
      {pixels.map((colour, index) => (
        <rect key={index} x={index % 8} y={Math.floor(index / 8)} width={1} height={1} fill={colour} />
      ))}
    </svg>
  );
}

// ------------------------------------------------------------------------ the area name

/**
 * An area's MiniMessage name, in its colours. The same partial reader the message editor previews
 * with (`blocks/minimessage.ts`), plus `<gradient:a:b>`, which an area name is the likeliest place to
 * use and which a browser CAN draw (as a text gradient). Anything else the reader cannot render is
 * dropped from view — a player is reading a title, not proofreading a template.
 */
export function AreaName({ source }: { readonly source: string }): React.JSX.Element {
  const parts: React.JSX.Element[] = [];
  const pattern = /<gradient:(#[0-9a-f]{6}):(#[0-9a-f]{6})(?::[^>]*)?>([\s\S]*?)(?:<\/gradient>|$)/gi;
  let last = 0;
  let key = 0;
  for (const match of source.matchAll(pattern)) {
    if (match.index > last) parts.push(<Styled key={key++} source={source.slice(last, match.index)} />);
    parts.push(
      <span key={key++} className="au-gradient-text" style={{ backgroundImage: `linear-gradient(90deg, ${match[1]}, ${match[2]})` }}>
        <Styled source={match[3] ?? ''} plain />
      </span>,
    );
    last = match.index + match[0].length;
  }
  if (last < source.length) parts.push(<Styled key={key++} source={source.slice(last)} />);
  return <>{parts}</>;
}

function Styled({ source, plain = false }: { readonly source: string; readonly plain?: boolean }): React.JSX.Element {
  const preview = parseMiniMessage(source);
  return (
    <>
      {preview.segments.map((segment, index) => {
        if (segment.kind === 'text') {
          const style = styleToCss(segment.style);
          const colour = segment.style.colour;
          return (
            <span key={index} style={plain ? {} : colour === null ? style : { ...style, color: legibleOnNight(colour) }}>
              {segment.text}
            </span>
          );
        }
        if (segment.kind === 'placeholder') return <span key={index}>{segment.name}</span>;
        return null;
      })}
    </>
  );
}

/**
 * A chat colour, lifted just enough to read on the night sign the area name sits on.
 *
 * Area names are written in Minecraft's chat colours, which the game draws over a dark backdrop with a
 * drop shadow — so the page draws them the same way (`.au-area` is that backdrop). The darkest of them
 * (`dark_red`, `dark_blue`, `black`…) are hard to read even there, so a colour under 3:1 against the
 * sign — the WCAG floor for text this large — is mixed toward white until it clears it. Every other
 * colour is exactly what the config says.
 */
export function legibleOnNight(hex: string, backdrop = '#141034'): string {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const parse = (value: string): [number, number, number] => [1, 3, 5].map((at) => Number.parseInt(value.slice(at, at + 2), 16)) as [number, number, number];
  const luminance = ([r, g, b]: [number, number, number]): number => {
    const linear = (c: number): number => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
  };
  const floor = luminance(parse(backdrop));
  const base = parse(hex);
  for (let mix = 0; mix <= 1.0001; mix += 0.05) {
    const rgb = base.map((c) => Math.round(c + (255 - c) * mix)) as [number, number, number];
    if ((luminance(rgb) + 0.05) / (floor + 0.05) >= 3) return `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
  }
  return '#ffffff';
}

/** The name with every tag removed, for `aria-label`s and the document title. */
export function plainAreaName(source: string): string {
  return parseMiniMessage(source.replace(/<\/?gradient[^>]*>/gi, '')).plainText.trim();
}

// ------------------------------------------------------------------------- the visualiser

/** The analyser's view of the mix. A narrow shape so the component never touches the engine. */
export interface FrequencySource {
  readonly frequencyBinCount: number;
  getByteFrequencyData(array: Uint8Array<ArrayBuffer>): void;
}

const COLUMNS = 24;
const ROWS = 10;

/**
 * Pixel bars over the real mix, in the brand gradient. Decorative (`aria-hidden`); the now-playing text
 * says what is playing. Animated on `requestAnimationFrame`, which the browser stops in a hidden tab —
 * exactly when nobody is looking — and drawn once, still, under `prefers-reduced-motion`.
 */
export function Visualiser({ source, active, intense }: { readonly source: FrequencySource | null; readonly active: boolean; readonly intense: boolean }): React.JSX.Element {
  const canvas = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const element = canvas.current;
    const context = element?.getContext('2d') ?? null;
    if (element === null || context === null) return;
    const reduced = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const bins = new Uint8Array(new ArrayBuffer(source?.frequencyBinCount ?? 128));
    let frame = 0;
    let phase = 0;
    const colours = Array.from({ length: COLUMNS }, (_, column) => mix('#c084fc', '#60a5fa', column / (COLUMNS - 1)));

    const draw = (): void => {
      const width = element.width;
      const height = element.height;
      context.clearRect(0, 0, width, height);
      const cellW = width / COLUMNS;
      const cellH = height / ROWS;
      if (active && source !== null) source.getByteFrequencyData(bins);
      phase += 0.04;
      for (let column = 0; column < COLUMNS; column += 1) {
        let level: number;
        if (active && source !== null) {
          // Logarithmic, like hearing: the first columns are a bin each (the bass), the last ones span
          // the presence range. 48 of 128 bins is ~8 kHz at 44.1 kHz, which is where music's energy ends.
          const index = Math.min(bins.length - 1, Math.round(1 + (column / (COLUMNS - 1)) ** 1.7 * 47));
          level = ((bins[index] ?? 0) / 255) * (intense ? 1.1 : 1);
        } else {
          level = 0.12 + 0.06 * Math.sin(phase + column * 0.5);
        }
        const lit = Math.max(1, Math.min(ROWS, Math.round(level * ROWS)));
        for (let row = 0; row < lit; row += 1) {
          context.fillStyle = row === lit - 1 && lit > 2 ? '#e39a7e' : (colours[column] as string);
          context.globalAlpha = active ? 1 : 0.45;
          context.fillRect(Math.round(column * cellW + 1), Math.round(height - (row + 1) * cellH + 1), Math.ceil(cellW - 2), Math.ceil(cellH - 2));
        }
      }
      context.globalAlpha = 1;
      if (!reduced) frame = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(frame);
  }, [source, active, intense]);

  return <canvas ref={canvas} className="au-visualiser" width={COLUMNS * 12} height={ROWS * 8} aria-hidden="true" data-testid="audio-visualiser" />;
}

function mix(a: string, b: string, t: number): string {
  const pa = Number.parseInt(a.slice(1), 16);
  const pb = Number.parseInt(b.slice(1), 16);
  const channel = (shift: number): number => Math.round(((pa >> shift) & 0xff) * (1 - t) + ((pb >> shift) & 0xff) * t);
  return `rgb(${channel(16)}, ${channel(8)}, ${channel(0)})`;
}

// ---------------------------------------------------------------------------- the volume

const VOLUME_LABEL: Readonly<Record<AudioVolumeCategory, string>> = {
  master: 'Master',
  music: 'Music',
  battle: 'Battles',
  vehicle: 'Vehicle',
  alerts: 'Alerts',
  voice: 'Voices',
};

/**
 * The sliders (§9.5), in `AUDIO_VOLUME_CATEGORIES`' order. `vehicle` is the ride a player sits on — its
 * loops and its cues. `voice` is reserved for NPC lines and has no slider until they ship.
 */
export function VolumePanel({
  volumes,
  muted,
  onVolumes,
  onMuted,
}: {
  readonly volumes: AudioVolumes;
  readonly muted: boolean;
  readonly onVolumes: (volumes: AudioVolumes) => void;
  readonly onMuted: (muted: boolean) => void;
}): React.JSX.Element {
  return (
    <section className="au-card dc-frame" aria-labelledby="au-volume-title" data-testid="audio-volume">
      <h2 id="au-volume-title" className="au-card-title">
        Volume
      </h2>
      <div className="au-sliders">
        {AUDIO_VOLUME_CATEGORIES.filter((category) => category !== 'voice').map((category) => {
          const percent = Math.round(volumes[category] * 100);
          return (
            <label key={category} className="au-slider">
              <span className="au-slider-name">{VOLUME_LABEL[category]}</span>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={percent}
                aria-valuetext={`${percent} percent`}
                data-testid={`volume-${category}`}
                onChange={(event) => onVolumes({ ...volumes, [category]: Number(event.target.value) / 100 })}
              />
              <span className="au-slider-value" aria-hidden="true">
                {percent}%
              </span>
            </label>
          );
        })}
      </div>
      <button type="button" className={`dc-button ${muted ? '' : 'dc-button-quiet'}`} aria-pressed={muted} onClick={() => onMuted(!muted)} data-testid="audio-mute">
        {muted ? 'Unmute' : 'Mute'}
      </button>
      <p className="au-fineprint">Saved in this browser, on this device only.</p>
    </section>
  );
}
